import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import Title from "../components/header/Title";
import PaymentService, { IPayment } from "../services/PaymentService";
import { unwrapPage, formatDateArray, getErrorMessage } from "../services/apiUtils";

const statusColor = (status?: string) => {
  switch ((status || "").toUpperCase()) {
    case "SUCCESS":
    case "SUCCEEDED":
    case "COMPLETED":
      return "success";
    case "FAILED":
      return "error";
    case "PENDING":
      return "warning";
    default:
      return "default";
  }
};

const Payments: React.FC = () => {
  const [payments, setPayments] = useState<IPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const loadPayments = () => {
    setLoading(true);
    setError(null);
    PaymentService.filterPayments({ page, size: pageSize })
      .then((res: any) => {
        const { content, totalElements } = unwrapPage<IPayment>(res);
        setPayments(content);
        setTotalCount(totalElements);
      })
      .catch((err: any) => {
        setPayments([]);
        setTotalCount(0);
        setError(
          getErrorMessage(
            err,
            "Payments could not be loaded. Please make sure the Payments API is reachable."
          )
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadPayments();

  }, [page, pageSize]);

  const columns: GridColDef[] = [
    { field: "paymentId", headerName: "Payment ID", flex: 0.5 },
    { field: "orderId", headerName: "Order ID", flex: 0.5 },
    { field: "transactionId", headerName: "Transaction ID", flex: 1.4 },
    {
      field: "poNumber",
      headerName: "PO Number",
      flex: 1.2,
      valueFormatter: (params: any) => params.value || "-",
    },
    { field: "userName", headerName: "Customer", flex: 0.8 },
    {
      field: "totalAmount",
      headerName: "Amount",
      flex: 0.7,
      valueFormatter: (params: any) =>
        params.value != null
          ? `${params.row?.currency || "Rs."} ${params.value}`
          : "-",
    },
    {
      field: "paymentDate",
      headerName: "Date",
      flex: 0.9,
      valueFormatter: (params: any) => formatDateArray(params.value),
    },
    {
      field: "paymentStatus",
      headerName: "Status",
      flex: 0.7,
      renderCell: (params: any) => (
        <Chip
          label={params.value || "-"}
          color={statusColor(params.value) as any}
          size="small"
        />
      ),
    },
  ];

  return (
    <Box>
      <Title title="Payments" subtitle="Track payment status for customer orders" />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ height: 600, width: "100%", bgcolor: "#fff" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 8 }}>
            <CircularProgress />
          </Box>
        ) : payments.length === 0 && !error ? (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 8 }}>
            <Typography color="text.secondary">No payments found.</Typography>
          </Box>
        ) : (
          <DataGrid
            rows={payments}
            columns={columns}
            getRowId={(row) => row.paymentId}
            paginationMode="server"
            rowCount={totalCount}
            page={page}
            pageSize={pageSize}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        )}
      </Box>
    </Box>
  );
};

export default Payments;
